import { useAtom } from '@reatom/npm-react'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button, CircularLoader } from '@blade/ui-kit'
import { InfoMessage } from '@root/shared/ui-kit'

import { SetupOtpInstructionsActions } from './setup-otp-instructions-actions'

import type { CreateOtpReatom } from '@root/features/auth/setup-otp/atoms'

type Props = {
  model: CreateOtpReatom
  onShowQr: () => void
  onContinue: () => void
}

export const SetupOtpInstructions: React.FC<Props> = ({
  model,
  onShowQr,
  onContinue,
}) => {
  const { t } = useTranslation()

  const [state] = useAtom(model.createOtpRequestState)

  const renderActions = () => {
    if (AsyncStateN.isError(state)) {
      return <Error intent="error" variant="dt" />
    }
    if (AsyncStateN.isSuccess(state)) {
      return (
        <SetupOtpInstructionsActions
          googleAuthCode={state.value.googleAuthCode}
          onShowQr={onShowQr}
        />
      )
    }
    return (
      <div
        css={css`
          margin: 10px 0;
        `}
      >
        <CircularLoader size={24} />
      </div>
    )
  }

  return (
    <Root>
      <Instructions>
        <Block>
          <Left>
            <Order>1</Order>
            <Line />
          </Left>
          <Right>
            <Text>
              <Trans
                i18nKey="mfa.instruction_line_1"
                components={[<br key="br" />, <span key="span" />]}
              />
            </Text>
          </Right>
        </Block>
        <Block>
          <Left>
            <Order>2</Order>
            <Line />
          </Left>
          <Right>
            <Text>{t('mfa.instruction_line_2')}</Text>
            {renderActions()}
          </Right>
        </Block>
        <Block>
          <Left
            css={css`
              padding: 0;
              gap: 0;
            `}
          >
            <Order>3</Order>
          </Left>
          <Right
            css={css`
              padding: 0;
            `}
          >
            <Text>{t('mfa.instruction_line_3')}</Text>
          </Right>
        </Block>
      </Instructions>

      <Actions>
        <ContinueButton
          disabled={!AsyncStateN.isSuccess(state)}
          onClick={onContinue}
          variant="soft"
          size="xs"
          radius="rounded"
        >
          {t('common.continue')}
        </ContinueButton>
      </Actions>
    </Root>
  )
}

const Root = styled.div``

const Block = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 15px;
`

const Instructions = styled.div`
  place-self: center;
`

const Actions = styled.div`
  display: grid;
  place-items: center;
  justify-items: center;

  &::before {
    content: '';

    height: 1px;
    width: 100%;
    margin: 16px 0;

    opacity: 0.05;
    background: radial-gradient(
      50% 50% at 50% 50%,
      var(--text-12) 0%,
      var(--text-12) 80%,
      rgba(255, 255, 255, 0) 100%
    );
  }
`

const Text = styled.div`
  color: var(--textA-11);

  font-size: 11px;
  font-weight: 600;
  line-height: 16px;

  span {
    color: var(--text-12);
  }
`

const Left = styled.div`
  display: grid;
  justify-items: center;
  grid-template-rows: auto 1fr;
  padding-block: 0 6px;
  gap: 6px;
`

const Right = styled.div`
  padding-block: 2px 16px;
`

const Line = styled.div`
  width: 1px;
  background: var(--backgroundA-4);
  padding-block: 16px;
`

const Order = styled.div`
  background: var(--backgroundA-4);

  width: 27px;
  height: 27px;
  border-radius: 50%;

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  font-weight: 600;
  font-size: 12px;
  color: var(--text-12);

  &::after {
    content: ' ';
    position: absolute;

    width: 23.727px;
    height: 23.727px;
    top: 2px;
    left: 1.6px;

    border: 1px solid var(--text-12);
    border-radius: 50%;
    box-sizing: border-box;
  }
`

const ContinueButton = styled(Button)`
  padding-inline: 20px;
`

const Error = styled(InfoMessage)`
  padding-block: 30px 20px;
`
